/**
 * 공통 상태 관리 스토어
 * 목적: 전역 상태 및 검색 조건 관리
 */
import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useCommonApi } from '~/composables/useCommonApi';
import { useCollaborationStore } from '~/composables/useCollaborationStore';
import { getCookie } from '~/composables/useCommon';
import { sortYearOptionsDesc } from '~/composables/pa/sortYearOptionsDesc';

interface CodeItem {
  label: string;
  value: string;
}

interface CommonCode {
  codeId: string;
  codeNm: string;
}

interface UserInfo {
  userId: string;
  userNm: string;
  deptNm: string;
  deptFullNm: string;
}

export const useCommonStore = defineStore('common', () => {
  // State
  const defaultYear = String(new Date().getFullYear());
  const initialYear = import.meta.client ? getCookie('findYear') || defaultYear : defaultYear;
  const findYear = ref(initialYear);
  const initialEvalCycleCd = import.meta.client ? getCookie('findEvalCycleCd') || '' : '';
  const findEvalCycleCd = ref(initialEvalCycleCd);
  const initialTotDegreeId = import.meta.client ? getCookie('findTotDegreeId') || '' : '';
  const findTotDegreeId = ref(initialTotDegreeId);
  const yearList = ref<CodeItem[]>([]);
  const monthList = ref<CodeItem[]>([
    { label: '1월', value: '01' },
    { label: '2월', value: '02' },
    { label: '3월', value: '03' },
    { label: '4월', value: '04' },
    { label: '5월', value: '05' },
    { label: '6월', value: '06' },
    { label: '7월', value: '07' },
    { label: '8월', value: '08' },
    { label: '9월', value: '09' },
    { label: '10월', value: '10' },
    { label: '11월', value: '11' },
    { label: '12월', value: '12' },
  ]);
  const codeJikgubList = ref<CodeItem[]>([]);
  const codePosList = ref<CodeItem[]>([]);
  const codeJobList = ref<CodeItem[]>([]);

  // 내부 Promise (외부 노출 안함)
  let yearPromise: Promise<CodeItem[]> | null = null;
  let jikgubPromise: Promise<CodeItem[]> | null = null;
  let posPromise: Promise<CodeItem[]> | null = null;
  let jobPromise: Promise<CodeItem[]> | null = null;

  const userNoPagingList = ref<UserInfo[]>([]);
  let userNoPagingPromise: Promise<UserInfo[]> | null = null;

  // 년도 목록 조회
  async function getYearList(): Promise<CodeItem[]> {
    if (yearList.value.length > 0) {
      return yearList.value;
    }

    if (yearPromise) {
      return yearPromise;
    }

    yearPromise = (async () => {
      try {
        const { fetchCommonCdList } = useCommonApi();
        const params = new URLSearchParams();
        params.append('codeGrpId', '017');

        const result = await fetchCommonCdList(params);
        if (result?.list) {
          yearList.value = sortYearOptionsDesc(
            result.list.map((item: CommonCode) => ({
              label: item.codeNm,
              value: item.codeId,
            })),
          );
        }
        return yearList.value;
      } catch (error) {
        console.error('년도 목록 조회 실패:', error);
        return [];
      } finally {
        yearPromise = null;
      }
    })();

    return yearPromise;
  }

  // 직급 목록 조회
  async function getCodeJikgubList(): Promise<CodeItem[]> {
    if (codeJikgubList.value.length > 0) {
      return codeJikgubList.value;
    }

    if (jikgubPromise) {
      return jikgubPromise;
    }

    jikgubPromise = (async () => {
      try {
        const { fetchCommonCdList } = useCommonApi();
        const params = new URLSearchParams();
        params.append('codeGrpId', '345');

        const result = await fetchCommonCdList(params);
        if (result?.list) {
          codeJikgubList.value = result.list.map((item: CommonCode) => ({
            label: item.codeNm,
            value: item.codeId,
          }));
        }
        return codeJikgubList.value;
      } catch (error) {
        console.error('직급 목록 조회 실패:', error);
        return [];
      } finally {
        jikgubPromise = null;
      }
    })();

    return jikgubPromise;
  }

  // 직책 목록 조회
  async function getCodePosList(): Promise<CodeItem[]> {
    if (codePosList.value.length > 0) {
      return codePosList.value;
    }

    if (posPromise) {
      return posPromise;
    }

    posPromise = (async () => {
      try {
        const { fetchCommonCdList } = useCommonApi();
        const params = new URLSearchParams();
        params.append('codeGrpId', '344');

        const result = await fetchCommonCdList(params);
        if (result?.list) {
          codePosList.value = result.list.map((item: CommonCode) => ({
            label: item.codeNm,
            value: item.codeId,
          }));
        }
        return codePosList.value;
      } catch (error) {
        console.error('직책 목록 조회 실패:', error);
        return [];
      } finally {
        posPromise = null;
      }
    })();

    return posPromise;
  }

  // 직위 목록 조회
  async function getCodeJobList(): Promise<CodeItem[]> {
    if (codeJobList.value.length > 0) {
      return codeJobList.value;
    }

    if (jobPromise) {
      return jobPromise;
    }

    jobPromise = (async () => {
      try {
        const { fetchCommonCdList } = useCommonApi();
        const params = new URLSearchParams();
        params.append('codeGrpId', '345');

        const result = await fetchCommonCdList(params);
        if (result?.list) {
          codeJobList.value = result.list.map((item: CommonCode) => ({
            label: item.codeNm,
            value: item.codeId,
          }));
        }
        return codeJobList.value;
      } catch (error) {
        console.error('직위 목록 조회 실패:', error);
        return [];
      } finally {
        jobPromise = null;
      }
    })();

    return jobPromise;
  }

  async function ensureUserNoPagingList(): Promise<UserInfo[]> {
    if (userNoPagingList.value.length > 0) {
      return userNoPagingList.value;
    }

    if (userNoPagingPromise) {
      return userNoPagingPromise;
    }

    userNoPagingPromise = (async () => {
      try {
        const { fetchUserAllNoPagingList } = useCommonApi();
        const params = new URLSearchParams();
        params.append('isIncludeDept', 'Y');
        params.append('retireViewYn', 'Y');

        const result = await fetchUserAllNoPagingList(params);
        userNoPagingList.value = (result?.list || []) as UserInfo[];
        return userNoPagingList.value;
      } catch (error) {
        console.error('사용자 목록 조회 실패:', error);
        return [];
      } finally {
        userNoPagingPromise = null;
      }
    })();

    return userNoPagingPromise;
  }

  function findUserNmById(userId: string): string {
    if (!userId) return '';

    if (userNoPagingList.value.length === 0) {
      void ensureUserNoPagingList();
      return '';
    }

    const user = userNoPagingList.value.find(u => String(u.userId) === String(userId));
    return user?.userNm ?? '';
  }

  // 사용자 팀명 조회 (전체 경로)
  function getDeptFullNmByUserId(userId: string): string {
    const { userList } = useCollaborationStore();
    if (!userList.value || !userId) {
      return '';
    }
    const user = (userList.value as UserInfo[]).find(u => u.userId === userId);
    return user?.deptFullNm ?? '';
  }

  // 사용자명 조회
  function getUserNm(userId: string): string {
    return findUserNmById(userId);
  }

  // 년도 변경 시 로컬스토리지·쿠키 동기화 (탭/페이지 간 공유, ASIS 쿠키 호환, path=/ 로 사이트 전역 공유)
  if (import.meta.client) {
    watch(
      findYear,
      newVal => {
        window.localStorage.setItem('commonFindYear', newVal);
        document.cookie = `findYear=${encodeURIComponent(newVal)};path=/`;
      },
      { immediate: false },
    );

    watch(
      findEvalCycleCd,
      newVal => {
        window.localStorage.setItem('commonFindEvalCycleCd', newVal);
        document.cookie = `findEvalCycleCd=${encodeURIComponent(newVal)};path=/`;
      },
      { immediate: false },
    );

    watch(
      findTotDegreeId,
      newVal => {
        window.localStorage.setItem('commonFindTotDegreeId', newVal);
        document.cookie = `findTotDegreeId=${encodeURIComponent(newVal)};path=/`;
      },
      { immediate: false },
    );
  }

  // $reset 대체 함수
  function $reset() {
    findYear.value = defaultYear;
    findEvalCycleCd.value = '';
    findTotDegreeId.value = '';
    yearList.value = [];
    codeJikgubList.value = [];
    codePosList.value = [];
    codeJobList.value = [];
  }

  // Computed - yearOptions (yearList의 별칭, 항상 API 호출)
  const yearOptions = computed(() => {
    getYearList();
    return yearList.value;
  });

  return {
    // State
    findYear,
    findEvalCycleCd,
    findTotDegreeId,
    yearList,
    monthList,
    codeJikgubList,
    codePosList,
    codeJobList,
    // Computed
    yearOptions,
    // Actions
    getYearList,
    getCodeJikgubList,
    getCodePosList,
    getCodeJobList,
    getDeptFullNmByUserId,
    getUserNm,
    $reset,
  };
});
